import { useEffect, useMemo, useState } from "react";
import { Cog, Lock, Shield, SlidersHorizontal, Sparkles } from "lucide-react";
import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionRoomStateCard } from "@/components/aion/AionRoomStateCard";
import { AionClientPlatformPanel } from "@/components/aion/AionClientPlatformPanel";
import { AionSpatialHomePanel } from "@/components/aion/AionSpatialHomePanel";
import { AionAutomationPanel } from "@/components/aion/AionAutomationPanel";
import { PersonalHealthPanel } from "@/components/aion/PersonalHealthPanel";
import { VehicleTelemetryPanel } from "@/components/aion/VehicleTelemetryPanel";
import { ActionReconciliationPanel } from "@/components/aion/ActionReconciliationPanel";
import { TrustCenterPanel } from "@/components/aion/TrustCenterPanel";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { DevicePanel } from "@/components/DevicePanel";
import { ScenePanel } from "@/components/ScenePanel";
import { EnergyPanel } from "@/components/EnergyPanel";
import { AgentPanel } from "@/components/AgentPanel";
import { ConsciousStream } from "@/components/ConsciousStream";
import { LogStream } from "@/components/LogStream";
import { ObservabilityPanel } from "@/components/ObservabilityPanel";
import { PersonalIntelligencePanel } from "@/components/PersonalIntelligencePanel";
import { SystemStatus } from "@/components/SystemStatus";
import { AionAdminWorkspace } from "@/components/aion/AionAdminWorkspace";
import { getHardwarePhysicalHub } from "@/services/api";

const PLATFORM_STAFF_ROLES = new Set(["PLATFORM_ADMIN", "MANAGER", "SALES_EMPLOYEE", "INSTALLER", "TECHNICIAN", "SUPPORT"]);

const sectionOptions = (isAdmin, operatorRole) => {
  const base = [
    { id: "devices", label: "ZUHAUSE & GERÄTE" },
    { id: "spatial", label: "RÄUME" },
    { id: "cameras", label: "KAMERAS" },
    { id: "robotics", label: "ROBOTER" },
    { id: "scenes", label: "SZENEN" },
    { id: "communication", label: "KOMMUNIKATION" },
    { id: "appointments", label: "TERMINE" },
    { id: "tasks", label: "AUFGABEN" },
    { id: "projects", label: "PROJEKTE" },
    { id: "health", label: "HEALTH" },
    { id: "vehicle", label: "FAHRZEUG" },
    { id: "market", label: "MARKT" },
    { id: "automations", label: "AUTOMATIONEN" },
    { id: "energy", label: "ENERGIE" },
    { id: "settings", label: "EINSTELLUNGEN" },
    { id: "system", label: "SYSTEM" },
    { id: "privacy", label: "DATENSCHUTZ" },
  ];
  if (!isAdmin) return base;
  const adminSections = [...base, { id: "admin", label: "ADMIN" }];
  return PLATFORM_STAFF_ROLES.has(String(operatorRole || "").toUpperCase())
    ? [...adminSections, { id: "advanced", label: "ERWEITERT" }]
    : adminSections;
};

const splitCsv = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);

const PHYSICAL_BLOCKER_LABELS = {
  NO_ROBOT_REGISTERED: "Kein Roboter registriert",
  ROBOT_SDK_ADAPTER_MISSING: "Dedizierter Robot-SDK-Adapter fehlt",
  REAL_EXECUTION_DISABLED: "Echte Ausführung ist deaktiviert",
  AUTONOMOUS_MISSIONS_DISABLED: "Autonome Missionen sind deaktiviert",
  ROBOT_VISION_INPUT_NOT_READY: "Kein Online-Vision-Eingang verfügbar",
};

export const MoreView = ({
  activeSection,
  home,
  adminData,
  compactHomeStatus,
  devices,
  energyDevices,
  energyForecast,
  energyStatus,
  intelligence,
  isAdmin,
  memory,
  marketIntelligence,
  onOpenProjects,
  onOptimizeEnergy,
  onSaveMemoryPreferences,
  onSectionChange,
  onRefresh,
  profileLabel,
  profileAccessMode,
  profileScopeKey,
  scenes,
  status,
  token,
  operatorRole,
  trustCenter,
  trustCenterStatus,
  trustCenterError,
  onRetryTrustCenter,
  voiceDiagnostics,
  voiceSettings,
}) => {
  const [settingsDraft, setSettingsDraft] = useState({
    favoriteDevices: "",
    favoriteScenes: "",
    automations: "",
  });
  const [physicalHub, setPhysicalHub] = useState(null);
  const [physicalHubError, setPhysicalHubError] = useState("");
  const canUseAdvanced = PLATFORM_STAFF_ROLES.has(String(operatorRole || "").toUpperCase());

  useEffect(() => {
    let active = true;
    if (activeSection !== "robotics") return undefined;
    setPhysicalHubError("");
    getHardwarePhysicalHub(token)
      .then((payload) => {
        if (active) setPhysicalHub(payload);
      })
      .catch((error) => {
        if (active) {
          setPhysicalHub(null);
          setPhysicalHubError(String(error?.message || "Physical Hub konnte nicht geladen werden."));
        }
      });
    return () => {
      active = false;
    };
  }, [activeSection, token]);

  useEffect(() => {
    setSettingsDraft({
      favoriteDevices: (memory?.favorite_devices || []).join(", "),
      favoriteScenes: (memory?.favorite_scenes || []).join(", "),
      automations: (memory?.personalized_automations || []).join(", "),
    });
  }, [memory]);

  const content = useMemo(() => {
    if (["tasks", "projects", "appointments", "communication"].includes(activeSection)) {
      const collections = {
        tasks: { title: "Aufgaben", items: home?.open_tasks },
        projects: { title: "Projekte", items: home?.active_projects },
        appointments: { title: "Termine", items: home?.appointments },
        communication: { title: "Kommunikation", items: home?.messages },
      };
      const { title, items } = collections[activeSection];
      return <AionSectionCard
        title={title}
        action={activeSection === "projects" ? <Button size="sm" variant="outline" onClick={() => onOpenProjects?.()} data-testid="more-open-project-workspace">Projekt-Workspace öffnen</Button> : null}
        testId={`more-${activeSection}-card`}
      >
        {Array.isArray(items) && items.length ? <div className="aion-list">{items.map((item, index) => <div className="aion-list-row" key={item.id || index}><div><strong>{item.title || item.subject || item.name}</strong><small>{item.summary || item.description || item.start_at || ""}</small></div></div>)}</div> : <AionEmptyState title={["appointments", "communication"].includes(activeSection) && !Array.isArray(items) ? "Nicht verbunden" : "Noch keine Einträge"} description="Gespeicherte Einträge erscheinen hier." />}
      </AionSectionCard>;
    }
    if (activeSection === "market") {
      const summary = marketIntelligence?.summary || {};
      const learning = marketIntelligence?.learning || {};
      const connector = marketIntelligence?.status || {};
      const latest = summary?.latest || null;
      const historicalFit = summary?.historical_fit || null;
      const bestSetup = learning?.best_supported_setup || null;
      const scenarioLabel = latest?.scenario === "LONG_SCENARIO"
        ? "LONG-SZENARIO"
        : latest?.scenario === "SHORT_SCENARIO"
          ? "SHORT-SZENARIO"
          : "WAIT";
      const confidence = latest?.confidence == null ? null : Math.round(Number(latest.confidence) * 100);
      return (
        <div className="aion-view aion-view-stack" data-testid="more-market-section">
          <AionSectionCard
            title="Market Intelligence"
            eyebrow="AION MARKET · READ ONLY"
            subtitle="MT5-Marktdaten analysieren, Szenarien speichern und Paper-Ergebnisse auswerten. Keine Broker-Orders, keine automatische Echtgeld-Ausführung."
            testId="market-intelligence-card"
          >
            <div className="aion-compact-status-grid" data-testid="market-intelligence-status-grid">
              <div className="aion-compact-status-box" data-testid="market-mode-box"><span>Modus</span><strong>READ ONLY</strong></div>
              <div className="aion-compact-status-box" data-testid="market-execution-box"><span>Live-Ausführung</span><strong>{connector?.execution_enabled ? "AN" : "AUS"}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-bridge-box"><span>MT5 Bridge</span><strong>{connector?.bridge_required ? "ERFORDERLICH" : "VERBUNDEN"}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-analysis-count-box"><span>Analysen</span><strong>{summary?.analyses ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-paper-count-box"><span>Paper Signale</span><strong>{summary?.evaluated_directional_signals ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-win-rate-box"><span>Paper Winrate</span><strong>{summary?.paper_win_rate == null ? "—" : `${summary.paper_win_rate}%`}</strong></div>
            </div>
          </AionSectionCard>
          <AionSectionCard
            title="Paper Learning"
            eyebrow="MARKET MEMORY"
            subtitle="AION wertet abgeschlossene Paper-Signale statistisch aus. Keine automatische Echtgeld-Ausführung und kein automatisches Modell-Retraining."
            testId="market-learning-card"
          >
            <div className="aion-compact-status-grid" data-testid="market-learning-grid">
              <div className="aion-compact-status-box" data-testid="market-learning-samples-box"><span>Samples</span><strong>{learning?.samples ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-learning-winrate-box"><span>Winrate</span><strong>{learning?.win_rate == null ? "—" : `${learning.win_rate}%`}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-learning-wins-box"><span>Wins</span><strong>{learning?.wins ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="market-learning-losses-box"><span>Losses</span><strong>{learning?.losses ?? 0}</strong></div>
            </div>
            {bestSetup ? (
              <div className="aion-note" data-testid="market-learning-best-setup">
                <strong>Bestes ausreichend getestetes Setup</strong>
                <p>{bestSetup.symbol} · {bestSetup.timeframe} · {bestSetup.scenario} · Alignment {bestSetup.alignment_band} · {bestSetup.samples} Samples · {bestSetup.win_rate}% Winrate</p>
              </div>
            ) : (
              <div className="aion-note" data-testid="market-learning-insufficient-note">
                Noch nicht genug Paper-Samples für ein belastbares Setup. Mindestens 5 vergleichbare abgeschlossene Signale sind erforderlich.
              </div>
            )}
          </AionSectionCard>
          <AionSectionCard
            title={latest ? `${latest.symbol} · ${latest.timeframe}` : "Letzte Marktanalyse"}
            eyebrow="LATEST ANALYSIS"
            subtitle={latest ? "Letzte gespeicherte Analyse aus AION Market Memory." : "Noch keine MT5-Marktanalyse gespeichert."}
            testId="market-latest-analysis-card"
          >
            {latest ? (
              <>
                <div className="aion-compact-status-grid" data-testid="market-latest-analysis-grid">
                  <div className="aion-compact-status-box" data-testid="market-scenario-box"><span>Szenario</span><strong>{scenarioLabel}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-confidence-box"><span>Confidence</span><strong>{confidence == null ? "—" : `${confidence}%`}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-trend-box"><span>Trend</span><strong>{latest.trend || "neutral"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-entry-box"><span>Referenz</span><strong>{latest.entry_reference ?? "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-support-box"><span>Support</span><strong>{latest.support ?? "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-resistance-box"><span>Widerstand</span><strong>{latest.resistance ?? "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-rsi-box"><span>RSI 14</span><strong>{latest.indicators?.rsi14 ?? "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-atr-box"><span>ATR 14</span><strong>{latest.indicators?.atr14 ?? "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-alignment-box"><span>MTF Alignment</span><strong>{latest.indicators?.alignment == null ? "—" : `${Math.round(Number(latest.indicators.alignment) * 100)}%`}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-higher-timeframe-box"><span>Höhere Zeitebene</span><strong>{latest.indicators?.higher_timeframe || "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-higher-scenario-box"><span>HTF Szenario</span><strong>{latest.indicators?.higher_timeframe_scenario || "—"}</strong></div>
                  <div className="aion-compact-status-box" data-testid="market-conflict-box"><span>Timeframe-Konflikt</span><strong>{latest.indicators?.conflict ? "JA" : "NEIN"}</strong></div>
                </div>
                {historicalFit ? (
                  <div className="aion-note" data-testid="market-historical-fit-note">
                    <strong>Historical Fit · Paper only</strong>
                    <p>
                      {historicalFit.samples ?? 0} vergleichbare abgeschlossene Signale ·
                      {" "}Winrate {historicalFit.win_rate == null ? "—" : `${historicalFit.win_rate}%`} ·
                      {" "}Evidenz {historicalFit.evidence_level || "insufficient"}
                    </p>
                  </div>
                ) : null}
                <div className="aion-note" data-testid="market-safety-note">
                  AION zeigt ein probabilistisches Szenario, keine sichere Kursprognose. Live-Trading und Order-Ausführung sind in diesem Modul deaktiviert.
                </div>
              </>
            ) : (
              <AionEmptyState title="Noch keine Marktdaten" description="Sobald die Read-Only-MT5-Bridge Kerzen an AION sendet, erscheint hier die Analyse." />
            )}
          </AionSectionCard>
        </div>
      );
    }

    if (activeSection === "robotics") {
      const readiness = physicalHub?.readiness || {};
      const robots = physicalHub?.robots || [];
      const blockers = physicalHub?.blockers || [];
      return (
        <div className="aion-view aion-view-stack" data-testid="more-robotics-section">
          <AionSectionCard
            title="AION Physical Hub"
            eyebrow="ROBOTICS · SAFE READ ONLY"
            subtitle="Read-only Systemcheck für zukünftige physische Roboter. Keine Bewegung, keine autonome Mission und keine reale Robot-Ausführung ist in diesem Bereich freigeschaltet."
            testId="physical-hub-card"
          >
            <div className="aion-compact-status-grid" data-testid="physical-hub-status-grid">
              <div className="aion-compact-status-box" data-testid="physical-hub-mode"><span>Modus</span><strong>{physicalHub?.mode || "SAFE READ ONLY"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-robots"><span>Roboter</span><strong>{readiness.registered_robots ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-online"><span>Online</span><strong>{readiness.online_robots ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-healthy"><span>Healthy</span><strong>{readiness.healthy_robots ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-sdk"><span>Robot SDK</span><strong>{physicalHub?.sdk_adapter_ready ? "BEREIT" : "FEHLT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-vision"><span>Vision</span><strong>{readiness.vision_input_available ? "BEREIT" : "NOCH NICHT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-telemetry"><span>Telemetrie</span><strong>{readiness.telemetry_ready ? "BEREIT" : "NOCH NICHT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-execution"><span>Real Execution</span><strong>{physicalHub?.real_execution_enabled ? "AN" : "AUS"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-autonomy"><span>Autonome Missionen</span><strong>{physicalHub?.autonomous_missions_enabled ? "AN" : "AUS"}</strong></div>
              <div className="aion-compact-status-box" data-testid="physical-hub-confirmation"><span>Bestätigung</span><strong>{physicalHub?.confirmation_required === false ? "NICHT NÖTIG" : "ERFORDERLICH"}</strong></div>
            </div>

            {physicalHubError ? <div className="aion-error-box" data-testid="physical-hub-error">{physicalHubError}</div> : null}

            <div className="aion-note" data-testid="physical-hub-safety-note">
              <strong>Trusted Action Boundary aktiv</strong>
              <p>Ein generischer MQTT-/Wi-Fi-Adapter gilt niemals automatisch als sicherer Robot-SDK-Adapter. Bewegung wird erst nach dediziertem SDK, Simulation, Sicherheitsprüfung und expliziter Bestätigung freigegeben.</p>
            </div>

            {blockers.length ? (
              <div className="aion-list" data-testid="physical-hub-blockers">
                {blockers.map((blocker) => (
                  <div className="aion-list-row" key={blocker}>
                    <div><strong>{PHYSICAL_BLOCKER_LABELS[blocker] || blocker}</strong><small>{blocker}</small></div>
                  </div>
                ))}
              </div>
            ) : null}
          </AionSectionCard>

          <AionSectionCard
            title="Registrierte physische Geräte"
            eyebrow="DEVICE PROFILES"
            subtitle="Nur Status und Fähigkeiten. Steuerung bleibt in diesem Physical-Hub-Bereich read-only."
            testId="physical-hub-robots-card"
          >
            {robots.length ? (
              <div className="aion-list" data-testid="physical-hub-robot-list">
                {robots.map((robot, index) => (
                  <div className="aion-list-row" key={robot.id || index} data-testid={`physical-hub-robot-${index}`}>
                    <div>
                      <strong>{robot.name || robot.id || "Roboter"}</strong>
                      <small>{String(robot.device_type || "robot").toUpperCase()} · {robot.connection_state || "unknown"} · {robot.health_state || "unknown"} · {robot.control_mode || "READ_ONLY"}</small>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <AionEmptyState
                title="Noch kein Roboter verbunden"
                description="Nach Auswahl eines konkreten Roboters wird hier zuerst ein dedizierter SDK-Adapter geprüft. Bis dahin bleibt Physical Hub sicher read-only."
                testId="physical-hub-empty"
              />
            )}
          </AionSectionCard>
        </div>
      );
    }

    if (activeSection === "cameras") {
      const types = ["camera", "ip_camera"];
      const rooms = (devices?.rooms || []).map(room => ({ ...room, devices: (room.devices || []).filter(device => types.includes(String(device.type || device.device_type || "").toLowerCase())) })).filter(room => room.devices.length);
      return <AionSectionCard
        title="Kameras & Vision-Status"
        eyebrow="AION VISION"
        subtitle="Verbundene Kameras und Erreichbarkeit. Privacy-sichere Vision-Analysen werden raumbezogen im Spatial Home angezeigt; Rohbilder werden nicht gespeichert."
        action={<Button size="sm" variant="outline" onClick={() => onSectionChange("spatial")} data-testid="camera-open-spatial-insights">Raum-Insights öffnen</Button>}
        testId="more-cameras-card"
      >
        {rooms.length ? <DevicePanel devices={{ rooms }} token={token} onDeviceUpdate={onRefresh} /> : <AionEmptyState title="Nicht verbunden" description="Es sind aktuell keine passenden Geräte registriert." />}
      </AionSectionCard>;
    }
    if (activeSection === "devices") {
      const roomCards = devices?.rooms || [];
      const featuredRooms = [...roomCards]
        .map((room) => {
          const roomDevices = room.devices || [];
          const activeDevices = roomDevices.filter((device) => String(device.state?.power || "").toLowerCase() === "on").length;
          const offlineDevices = roomDevices.filter((device) => device.connection_state === "offline").length;
          const signals = roomDevices.filter((device) => ["blind", "sensor", "light"].includes(String(device.type || device.device_type || ""))).length;
          return { room, score: (activeDevices * 4) + (offlineDevices * 3) + signals + (roomDevices.length * 0.1) };
        })
        .sort((left, right) => right.score - left.score || left.room.name.localeCompare(right.room.name))
        .slice(0, 8)
        .map((entry) => entry.room);
      const connectors = adminData?.hardwareConnectors?.connectors || [];
      return (
        <div className="aion-view aion-view-stack" data-testid="more-devices-section">
          <section className="aion-section-card aion-robotics-card" data-testid="more-robotics-brand-card">
            <div className="aion-robotics-layout" data-testid="more-robotics-layout">
              <div className="aion-robotics-copy" data-testid="more-robotics-copy">
                <p className="aion-section-eyebrow" data-testid="more-robotics-eyebrow">AION CONTROL</p>
                <h2 className="aion-section-title" data-testid="more-robotics-title">Zuhause, Geräte und Technik zentral steuern.</h2>
                <p className="aion-section-subtitle" data-testid="more-robotics-subtitle">Räume, Geräte, Sensoren und verbundene Systeme in einer klaren technischen Übersicht.</p>
              </div>
              <AionLogoArtwork
                variant="robotics"
                className="aion-robotics-logo-wrap"
                imageClassName="aion-robotics-logo"
                dataTestId="more-robotics-logo"
              />
            </div>
          </section>
          <AionSectionCard title="Zuhause & Geräte" eyebrow="ZUHAUSE" subtitle="Bestehende Smart-Home-Funktionen kompakt und erreichbar." testId="more-home-devices-card">
            <div className="aion-compact-status-grid" data-testid="more-home-status-grid">
              <div className="aion-compact-status-box" data-testid="more-home-status-active-devices"><span>Geräte aktiv</span><strong>{compactHomeStatus.activeDevices}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-home-status-rooms"><span>Räume</span><strong>{compactHomeStatus.roomCount}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-home-status-energy"><span>Energie</span><strong>{compactHomeStatus.currentPower}</strong></div>
            </div>
            <div className="aion-room-card-grid" data-testid="more-room-health-list">
              {featuredRooms.map((room, index) => (
                <AionRoomStateCard key={room.id || index} room={room} index={index} />
              ))}
            </div>
            <div className="aion-list" data-testid="more-adapter-status-list">
              {connectors.map((connector, index) => (
                <div className="aion-list-row" key={connector.id || index} data-testid={`more-adapter-status-item-${index}`}>
                  <div>
                    <strong>{connector.name}</strong>
                    <small>{connector.provider.toUpperCase()} · {connector.status} · {connector.connected_devices} Geräte</small>
                  </div>
                </div>
              ))}
            </div>
          </AionSectionCard>
          {isAdmin ? (
            <AionClientPlatformPanel
              devices={adminData?.aionDevices?.devices || []}
              overview={adminData?.aionDevicesOverview || null}
              rooms={devices?.rooms || []}
              sessions={adminData?.aionDeviceSessions?.sessions || []}
              token={token}
              onRefresh={onRefresh}
            />
          ) : null}
          <DevicePanel devices={devices} token={token} onDeviceUpdate={onRefresh} />
        </div>
      );
    }

    if (activeSection === "spatial") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-spatial-section">
          <AionSpatialHomePanel devices={devices} token={token} onRefresh={onRefresh} profileAccessMode={profileAccessMode} profileScopeKey={profileScopeKey} />
        </div>
      );
    }

    if (activeSection === "health") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-health-section">
          {profileAccessMode === "shared_view" ? (
            <AionEmptyState
              icon={Lock}
              title="Privater Health-Bereich gesperrt"
              description="Persönliche Gesundheitsdaten sind nur im eigenen Profil verfügbar. Wechsle zurück zu Deinem Profil und entsperre Personal Health dort erneut."
              testId="personal-health-shared-profile-locked"
            />
          ) : (
            <PersonalHealthPanel token={token} />
          )}
        </div>
      );
    }

    if (activeSection === "vehicle") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-vehicle-section">
          <VehicleTelemetryPanel devices={devices} token={token} />
        </div>
      );
    }

    if (activeSection === "automations") {
      return <AionAutomationPanel token={token} scenes={scenes} profileAccessMode={profileAccessMode} operatorRole={operatorRole} onChanged={onRefresh} />;
    }

    if (activeSection === "scenes") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-scenes-section">
          <AionSectionCard title="Szenen" eyebrow="SZENEN" subtitle="Szenen bleiben die sicheren Aktionsbausteine für manuelle Nutzung und Routinen." testId="more-scenes-card" />
          <ScenePanel scenes={scenes} devices={(devices?.rooms || []).flatMap((room) => room.devices || [])} token={token} onSceneChanged={onRefresh} />
        </div>
      );
    }

    if (activeSection === "energy") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-energy-section">
          <AionSectionCard title="Energie" eyebrow="ENERGIE" subtitle="Bestehende Energie-Pipeline – jetzt über MORE erreichbar." testId="more-energy-card">
            <div className="aion-note" data-testid="more-energy-note">AION zeigt hier die echte Energie-Lage aus dem vorhandenen Backend.</div>
          </AionSectionCard>
          {energyStatus ? (
            <EnergyPanel status={energyStatus} devices={energyDevices} forecast={energyForecast} token={token} onOptimize={onOptimizeEnergy} />
          ) : (
            <AionEmptyState icon={Sparkles} title="Energie aktuell nicht verfügbar." description="Wenn die Energie-API keine Daten liefert, bleibt dieser Bereich bewusst leer statt mit Demo-Daten gefüllt." testId="more-energy-empty" />
          )}
        </div>
      );
    }

    if (activeSection === "settings") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-settings-section">
          {voiceSettings}
          {profileAccessMode === "shared_view" ? (
            <AionEmptyState
              icon={Lock}
              title="Persönliche Memory-Einstellungen gesperrt"
              description="Favoriten, persönliche Szenen und Automationen können nur im eigenen Profil geändert werden."
              testId="memory-settings-shared-profile-locked"
            />
          ) : (
            <AionSectionCard
              title="Einstellungen"
              eyebrow="PERSÖNLICH"
              subtitle="Bestehende Memory-Präferenzen bleiben erhalten und werden hier übersichtlich bearbeitet."
              action={<Button onClick={() => onSaveMemoryPreferences({
                favorite_devices: splitCsv(settingsDraft.favoriteDevices),
                favorite_scenes: splitCsv(settingsDraft.favoriteScenes),
                personalized_automations: splitCsv(settingsDraft.automations),
              })} data-testid="memory-save-button">Speichern</Button>}
              testId="more-settings-card"
            >
              <div className="aion-settings-grid" data-testid="more-settings-grid">
                <label className="aion-settings-field" data-testid="favorite-devices-field">
                  <span>Lieblingsgeräte</span>
                  <Input value={settingsDraft.favoriteDevices} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, favoriteDevices: event.target.value }))} placeholder="Wohnzimmer Licht, TV" data-testid="favorite-devices-input" />
                </label>
                <label className="aion-settings-field" data-testid="favorite-scenes-field">
                  <span>Lieblingsszenen</span>
                  <Input value={settingsDraft.favoriteScenes} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, favoriteScenes: event.target.value }))} placeholder="Movie Night, Focus" data-testid="favorite-scenes-input" />
                </label>
                <label className="aion-settings-field" data-testid="personalized-automations-field">
                  <span>Persönliche Automationen</span>
                  <Input value={settingsDraft.automations} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, automations: event.target.value }))} placeholder="Morning Routine" data-testid="personalized-automations-input" />
                </label>
              </div>
            </AionSectionCard>
          )}
        </div>
      );
    }

    if (activeSection === "system") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-system-section">
          <AionSectionCard title="System" eyebrow="AION" subtitle="Kompakte Systeminfos für normale Nutzung." testId="more-system-card">
            <div className="aion-compact-status-grid" data-testid="more-system-grid">
              <div className="aion-compact-status-box" data-testid="more-system-user-box"><span>Nutzer</span><strong>{status?.user?.name || status?.user?.email || "—"}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-devices-box"><span>Geräte</span><strong>{status?.counts?.devices ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-scenes-box"><span>Szenen</span><strong>{status?.counts?.scenes ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-activity-box"><span>Aktivität</span><strong>{status?.counts?.activity_logs ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-mode-box"><span>Local Core</span><strong>{adminData?.localCore?.identity?.mode || "HYBRID"}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-network-box"><span>Netz</span><strong>{adminData?.localCore?.connectivity?.internet_online ? "ONLINE" : "OFFLINE"}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-cloud-box"><span>Cloud</span><strong>{adminData?.localCore?.connectivity?.cloud_reachable ? "ERREICHBAR" : "NICHT ERREICHBAR"}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-queue-box"><span>Offline Queue</span><strong>{adminData?.localCore?.queue_size ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-sync-box"><span>Sync</span><strong>{adminData?.localCore?.health?.sync_state || adminData?.localCore?.identity?.sync_status || "SYNCED"}</strong></div>
              <div className="aion-compact-status-box" data-testid="more-system-local-core-health-box"><span>Health</span><strong>{adminData?.localCore?.health?.status || adminData?.localCore?.identity?.health_status || "WARNING"}</strong></div>
            </div>
          </AionSectionCard>
        </div>
      );
    }

    if (activeSection === "privacy") {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-privacy-section">
          <ActionReconciliationPanel token={token} />
          <TrustCenterPanel trustCenter={trustCenter} trustCenterStatus={trustCenterStatus} trustCenterError={trustCenterError} onRetryTrustCenter={onRetryTrustCenter} token={token} onRefresh={onRefresh} isAdmin={isAdmin} />
        </div>
      );
    }

    if (activeSection === "advanced" && isAdmin && canUseAdvanced) {
      return (
        <div className="aion-view aion-view-stack" data-testid="more-advanced-section">
          <AionSectionCard title="Erweitert / Admin" eyebrow="ADMIN" subtitle="Bestehende Entwickler- und Admin-Panels bleiben vollständig erhalten, aber dominieren nicht mehr HOME." testId="more-advanced-card">
            <div className="aion-note" data-testid="more-advanced-note">Agenten, Logs, Observability und interne Systemflächen wurden bewusst hierhin verschoben.</div>
          </AionSectionCard>
          <AionSectionCard title="Sprachdiagnose" eyebrow="SPRACHE" subtitle="Nur Admin: aktuelle Voice-Session ohne unnötige Audiodauerablage." testId="more-voice-diagnostics-card">
            <div className="aion-compact-status-grid" data-testid="more-voice-diagnostics-grid">
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-session-box"><span>Session</span><strong>{voiceDiagnostics?.session_id ? voiceDiagnostics.session_id.slice(-8) : "—"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-state-box"><span>Sprachstatus</span><strong>{voiceDiagnostics?.display_state || "BEREIT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-language-box"><span>Sprache</span><strong>{voiceDiagnostics?.detected_language || memory?.preferred_language || "auto"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-stt-box"><span>STT</span><strong>{voiceDiagnostics?.stt_provider || "groq"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-tts-box"><span>TTS</span><strong>{voiceDiagnostics?.tts_provider || "cartesia"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-llm-health-box"><span>Brain aktiv</span><strong>{voiceDiagnostics?.provider_health?.llm?.primary || "groq"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-groq-health-box"><span>Groq Brain</span><strong>{voiceDiagnostics?.provider_health?.llm?.providers?.groq?.configured ? "BEREIT" : "NICHT KONFIGURIERT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-openrouter-health-box"><span>OpenRouter Backup</span><strong>{voiceDiagnostics?.provider_health?.llm?.providers?.openrouter?.configured ? "BEREIT" : "NICHT KONFIGURIERT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-cartesia-health-box"><span>Cartesia</span><strong>{voiceDiagnostics?.provider_health?.tts?.providers?.cartesia?.configured ? "BEREIT" : "NICHT KONFIGURIERT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-elevenlabs-health-box"><span>ElevenLabs Backup</span><strong>{voiceDiagnostics?.provider_health?.tts?.providers?.elevenlabs?.configured ? "BEREIT" : "NICHT KONFIGURIERT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-wake-mode-box"><span>Wake-Modus</span><strong>{voiceDiagnostics?.wake_word_mode || "WAKE_WORD_DISABLED"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-browser-box"><span>Browser</span><strong>{voiceDiagnostics?.browser_family || "unknown"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-capture-box"><span>Capture</span><strong>{voiceDiagnostics?.capture_strategy || "unsupported"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-interruptions-box"><span>Unterbrechungen</span><strong>{voiceDiagnostics?.interruption_count ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-fallback-box"><span>Fallback</span><strong>{voiceDiagnostics?.fallback_used ? "ja" : "nein"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-last-llm-box"><span>Brain zuletzt</span><strong>{voiceDiagnostics?.provider_activity?.llm?.provider || "—"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-last-tts-box"><span>TTS zuletzt</span><strong>{voiceDiagnostics?.provider_activity?.tts?.provider || "—"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-last-fallback-box"><span>Provider-Fallback</span><strong>{voiceDiagnostics?.provider_activity?.llm?.fallback_used || voiceDiagnostics?.provider_activity?.tts?.fallback_used ? "ja" : "nein"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-groq-metrics-box"><span>Groq Fehler</span><strong>{(voiceDiagnostics?.provider_metrics?.llm?.groq?.failures ?? 0) + (voiceDiagnostics?.provider_metrics?.stt?.groq?.failures ?? 0)}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-openrouter-metrics-box"><span>OpenRouter Fallbacks</span><strong>{voiceDiagnostics?.provider_metrics?.llm?.openrouter?.fallbacks ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-cartesia-metrics-box"><span>Cartesia Fehler</span><strong>{voiceDiagnostics?.provider_metrics?.tts?.cartesia?.failures ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-elevenlabs-metrics-box"><span>ElevenLabs Fallbacks</span><strong>{voiceDiagnostics?.provider_metrics?.tts?.elevenlabs?.fallbacks ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-live-ready-box"><span>Live-Test</span><strong>{voiceDiagnostics?.provider_configuration?.ready_for_live_voice ? "BEREIT" : "NOCH NICHT"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-missing-config-box"><span>Fehlende Provider-Daten</span><strong>{voiceDiagnostics?.provider_configuration?.missing?.length ?? 0}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-error-box"><span>Fehlercode</span><strong>{voiceDiagnostics?.error_code || "—"}</strong></div>
              <div className="aion-compact-status-box" data-testid="voice-diagnostics-turn-latency-box"><span>Antwortzeit</span><strong>{voiceDiagnostics?.latency?.full_turn_ms ?? "—"} ms</strong></div>
            </div>
            {(voiceDiagnostics?.provider_configuration?.missing || []).length ? (
              <div className="aion-note" data-testid="voice-diagnostics-missing-config-list">
                <strong>Noch in Railway setzen</strong>
                <p>{voiceDiagnostics.provider_configuration.missing.join(" · ")}</p>
              </div>
            ) : null}
          </AionSectionCard>
          <SystemStatus status={status} health={adminData?.health} />
          <PersonalIntelligencePanel intelligence={intelligence} token={token} onRefresh={onRefresh} memory={memory} />
          <AgentPanel agents={adminData?.agents} logs={adminData?.agentLogs} consciousness={adminData?.consciousness} token={token} onAgentRefresh={onRefresh} />
          <ConsciousStream consciousness={adminData?.consciousness} predictions={adminData?.predictions} />
          <ObservabilityPanel observability={adminData?.observability} />
          <LogStream events={adminData?.events || []} decisions={adminData?.decisionLog || []} observabilityLogs={adminData?.observability?.logs?.logs || adminData?.observability?.logs || []} />
        </div>
      );
    }

    if (activeSection === "admin" && isAdmin) {
      return (
        <AionAdminWorkspace
          customers={adminData?.platformCustomers || []}
          dashboard={adminData?.platformDashboard || null}
          employees={adminData?.platformEmployees || []}
          hardware={adminData?.platformHardware || []}
          home={adminData?.platformHome || null}
          installations={adminData?.platformInstallations || []}
          operatorRole={operatorRole}
          roles={adminData?.platformRoles || []}
          systemSummary={adminData?.platformSystemSummary || null}
          token={token}
          users={adminData?.platformUsers || []}
          onRefresh={onRefresh}
        />
      );
    }

    return <AionEmptyState icon={SlidersHorizontal} title="Bereich auswählen" description="Wähle links oder unten einen MORE-Bereich aus." testId="more-section-empty" />;
  }, [activeSection, home, adminData, canUseAdvanced, compactHomeStatus, devices, energyDevices, energyForecast, energyStatus, intelligence, isAdmin, marketIntelligence, memory, onOpenProjects, onOptimizeEnergy, onRefresh, onRetryTrustCenter, onSaveMemoryPreferences, onSectionChange, operatorRole, profileAccessMode, profileScopeKey, scenes, status, token, trustCenter, trustCenterError, trustCenterStatus, settingsDraft, physicalHub, physicalHubError, voiceDiagnostics, voiceSettings]);

  return (
    <div className="aion-view aion-view-stack" data-testid="more-view">
      <section className="aion-section-card" data-testid="more-menu-card">
        <div className="aion-section-header">
          <div>
            <p className="aion-section-eyebrow" data-testid="more-menu-eyebrow">AION CONTROL CENTER</p>
            <h2 className="aion-section-title" data-testid="more-menu-title">Alle Systeme an einem Ort</h2>
            <p className="aion-section-subtitle" data-testid="more-menu-subtitle">Öffne Räume, Geräte, Kameras, Energie, Fahrzeug, Roboter und persönliche Einstellungen.</p>
          </div>
        </div>
        <div className="aion-note" data-testid="more-profile-note">Aktiver Bereich: {profileLabel || "Mein Bereich"}</div>
        <div className="aion-section-nav" data-testid="more-section-nav">
          {sectionOptions(isAdmin, operatorRole).map((section) => (
            <button type="button" key={section.id} className={`aion-section-nav-item${activeSection === section.id ? " aion-section-nav-item-active" : ""}`} onClick={() => onSectionChange(section.id)} data-testid={`more-section-${section.id}-button`}>
              {section.label}
            </button>
          ))}
        </div>
      </section>
      <div className="aion-more-section-frame" data-testid="more-section-frame" key={activeSection}>{content}</div>
    </div>
  );
};
